import { Component, Input, OnInit } from '@angular/core';
import { MemberRole } from '@app/core/interfaces/c5/enumerations';
import { MeetingUpdateMember } from '@app/core/interfaces/portal/meeting-update-member';
import { ReactiveFormArrayComponent } from '@app/shared/components/reactive-form-array.component';
import { ContactNamePipe } from '@app/shared/pipes/contact-name.pipe';
import { TypeaheadEntry } from '@app/shared/services/typeahead-basket.service';
import { FormMapping } from '@app/utils/form-mapping/decorators/form-mapping';
import { FormMappingAttribute } from '@app/utils/form-mapping/decorators/form-mapping-attribute';
import { ArrayInput, StringInput } from '@app/utils/form-mapping/helpers/default-attributes';
import { FormMappingModel } from '@app/utils/form-mapping/models/form-mapping-model';
import { ngValueAccessor } from '@app/utils/ng-value-accessor';
import { sortBy } from '@app/utils/sort/sort-by';
import { EndUserLite } from '@business/end-user/models/end-user-lite';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormArrayTyped } from '../../../../../types/typed-forms';

@UntilDestroy()
@Component({
  selector: 'portal-update-meeting-members',
  templateUrl: './update-meeting-members.component.html',
  styles: [],
  providers: [ngValueAccessor(UpdateMeetingMembersComponent)],
  host: {
    class: 'update-meeting__members',
  },
  standalone: false,
})
export class UpdateMeetingMembersComponent
  extends ReactiveFormArrayComponent<MeetingUpdateMember, MembersForm, MemberForm>
  implements OnInit
{
  @Input()
  userMap: Record<number, EndUserLite>;

  MemberRole = MemberRole;

  memberRoles: TypeaheadEntry[] = [
    {
      label: MemberRole.ADMIN,
      value: MemberRole.ADMIN,
    },
    {
      label: MemberRole.USER,
      value: MemberRole.USER,
    },
  ];

  users: TypeaheadEntry<number>[] = [];

  constructor(private contactNamePipe: ContactNamePipe) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.form
      .get('userId')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((userId: number | null | '') => {
        if (userId != null && userId !== '') {
          this.add({
            role: MemberRole.USER,
            userId,
          });

          this.form.get('userId').setValue(null);
          this.updateAvailableUsers();
        }
      });
  }

  protected arrayFormModel = MembersForm;
  protected entryFormModel = MemberForm;
  protected isIndexed: boolean = false;
  protected parameterName: string = 'members';

  protected getFormArray(): FormArrayTyped<MemberForm> {
    return this.form.get('members') as any;
  }

  protected newEntry(): MeetingUpdateMember {
    return {
      role: MemberRole.USER,
      userId: 0,
    };
  }

  add(value?: MeetingUpdateMember) {
    super.add(value);
  }

  remove(index: number) {
    super.remove(index);

    this.updateAvailableUsers();
  }

  writeValue(obj: any) {
    super.writeValue(obj);

    this.updateAvailableUsers();
  }

  private updateAvailableUsers(): void {
    this.users = Object.values(this.userMap)
      .filter((user) => this.form.get('members').value.some((member) => member.userId === user.id) === false)
      .map((user) => {
        const contactName = this.contactNamePipe.transform(user, { type: 'long' });

        return {
          key: contactName,
          label: contactName,
          value: user.id,
        };
      })
      .sort(
        sortBy((entry) => {
          const user = this.userMap[entry.value];

          return `${user.siteId}:${user.loginName}`;
        })
      );

    if (this.users.length === 0) {
      this.form.get('userId').disable();
    } else {
      this.form.get('userId').enable();
    }
  }
}

@FormMapping()
export class MemberForm extends FormMappingModel {
  @StringInput
  role: string;

  @StringInput
  userId: string;
}

@FormMapping()
class MembersForm extends FormMappingModel {
  @ArrayInput(MemberForm, {
    allowDuplicates: false,
    uniqueValueFunction: (model) => model.userId,
  })
  members!: MembersForm[];

  @FormMappingAttribute({
    initialValue: null,
  })
  userId: number | null;
}
